import Vue from "vue";

// ─── Viewport awareness (single source) ────────────────────────────────
// One rAF-throttled resize listener drives an observable the whole app can
// read as `this.$viewport` (wired in main.js, mirroring sequenceState).
//
// Deliberate choices (see MOBILE_RESPONSIVE_PLAN.md eng review):
//  • `addEventListener('resize')` — NEVER `window.onresize`. The table-height
//    mixin (src/utils/mixin.js) owns the single `window.onresize` slot; a
//    second assignment would clobber ~17 views.
//  • The `isMobile` / `isTablet` booleans only change when a breakpoint is
//    crossed, so components watching them re-render at boundaries, not on
//    every pixel. `width` is exposed for the rare caller that needs it.
//  • Reactive util, not Vuex — avoids committing to the store on every
//    resize and fanning re-renders out to every subscriber.

// Keep in lockstep with src/styles/@responsive.scss ($bp-phone / $bp-tablet).
export const BP_PHONE = 767;
export const BP_TABLET = 1023;

const hasWindow = typeof window !== "undefined";
const initialWidth = hasWindow ? window.innerWidth : 1280;

const viewport = Vue.observable({
  width: initialWidth,
  isMobile: initialWidth <= BP_PHONE, // <= 767
  isTablet: initialWidth > BP_PHONE && initialWidth <= BP_TABLET, // 768–1023
  isDesktop: initialWidth > BP_TABLET, // >= 1024
});

function measure() {
  const w = window.innerWidth;
  const isMobile = w <= BP_PHONE;
  const isTablet = w > BP_PHONE && w <= BP_TABLET;
  const isDesktop = w > BP_TABLET;
  if (viewport.width !== w) viewport.width = w;
  if (viewport.isMobile !== isMobile) viewport.isMobile = isMobile;
  if (viewport.isTablet !== isTablet) viewport.isTablet = isTablet;
  if (viewport.isDesktop !== isDesktop) viewport.isDesktop = isDesktop;
}

let ticking = false;
function onResize() {
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(() => {
    ticking = false;
    measure();
  });
}

let started = false;
export function initViewport() {
  if (!hasWindow || started) return;
  started = true;
  measure();
  window.addEventListener("resize", onResize, { passive: true });
  // Orientation change on iOS can settle a frame after `resize`.
  window.addEventListener("orientationchange", onResize, { passive: true });
}

export default viewport;
